import { useMemo } from "react";
import { addDays, todayIn } from "../../../shared/dates";
import { kidsFirst } from "../../../shared/people";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useCalendarEvents, useMembers, useRewards } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { formatReward } from "../../ui/reward";
import { useNow } from "../../ui/time";
import { eventsOnDay, fmtDate, placeEvent } from "../calendar/calendarModel";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { useWeekSummary } from "./api";
import "./home.css";
import { remPx } from "../sky/common";

type SummaryConfig = Record<string, never>;

/**
 * The family's week, counted from what happened on the wall: chores and
 * routines by person, dinners planned, bedtime checklists finished, and
 * what's coming next week. Plain counts, no AI.
 */
function WeekSummary({ size, fullscreen, openFullscreen }: WidgetProps<SummaryConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const q = useWeekSummary();
  const members = useMembers().data ?? [];
  const currency = useRewards().data?.currency ?? "stars";
  const cal = useCalendarEvents(addDays(today, 1), addDays(today, 8));
  const state = dataStateOf(q, 2 * 60 * 60_000);

  const next = useMemo(() => {
    const placed = (cal.data?.events ?? []).filter((e) => e.status !== "cancelled").map((e) => placeEvent(e, timeZone));
    const days = Array.from({ length: 7 }, (_, i) => addDays(today, i + 1)).map((d) => ({ date: d, n: eventsOnDay(placed, d).length }));
    const busiest = days.reduce((a, b) => (b.n > a.n ? b : a), days[0]!);
    return { total: new Set(placed.map((p) => p.event.id)).size, busiest: busiest.n >= 2 ? busiest : null };
  }, [cal.data, timeZone, today]);

  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="star" title="Weekly summary unavailable" detail={q.error?.message} />;

  const byId = new Map(data.people.map((p) => [p.memberId, p]));
  const people = kidsFirst(members).filter((m) => {
    const p = byId.get(m.id);
    return p && (p.chores || p.routineSteps || p.earned);
  });
  const stats = [
    { value: data.choresDone, label: data.choresDone === 1 ? "chore done" : "chores done" },
    { value: data.tasksDone, label: data.tasksDone === 1 ? "task done" : "tasks done" },
    { value: data.dinnersPlanned, label: "dinners planned" },
    { value: data.nightsAllSet, label: data.nightsAllSet === 1 ? "night all set" : "nights all set" },
  ];
  const lines = [
    data.topChore && `Most-done chore: ${data.topChore.title} (${data.topChore.count}×)`,
    data.notesAdded > 0 && `${data.notesAdded} family ${data.notesAdded === 1 ? "note" : "notes"} posted`,
    next.total > 0 ? `Next 7 days: ${next.total} ${next.total === 1 ? "event" : "events"}${next.busiest ? `, busiest ${fmtDate(next.busiest.date, { weekday: "long" })} (${next.busiest.n})` : ""}` : "Next 7 days: a quiet calendar",
    data.dinnersNextWeek === 0 ? "No dinners planned for next week yet" : `${data.dinnersNextWeek} of 7 dinners planned for next week`,
  ].filter(Boolean) as string[];

  return (
    <div className="home-w week-summary">
      <WidgetHeader title="Our week" meta={`${fmtDate(data.from, { month: "short", day: "numeric" })} – ${fmtDate(data.to, { month: "short", day: "numeric" })}`} onTitle={fullscreen ? undefined : openFullscreen} />
      <div className="ws-stats">
        {stats.map((s) => (
          <span key={s.label} className="ws-stat">
            <strong>{s.value}</strong>
            <small>{s.label}</small>
          </span>
        ))}
      </div>
      {people.length > 0 && size.height > remPx() * 15.8 && (
        <ul className="ws-people">
          {people.map((m) => {
            const p = byId.get(m.id)!;
            return (
              <li key={m.id}>
                <Avatar member={m} size="1.9rem" />
                <span className="ws-name">{m.displayName}</span>
                <span className="muted">
                  {[p.chores && `${p.chores} ${p.chores === 1 ? "chore" : "chores"}`, p.routineSteps && `${p.routineSteps} routine steps`].filter(Boolean).join(" · ")}
                </span>
                {p.earned > 0 && <span className="ws-earned">+{formatReward(p.earned, currency)}</span>}
              </li>
            );
          })}
        </ul>
      )}
      <ul className="ws-lines">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
    </div>
  );
}

export const weekSummaryWidget: WidgetDefinition<SummaryConfig> = {
  type: "week-summary",
  version: 1,
  name: "Our week",
  description: "The last seven days at home: chores and routines by person, stars earned, dinners planned, bedtime checklists finished, and what’s coming up.",
  icon: "star",
  category: "household",
  defaultSize: { w: 5, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 400,
  defaults: {},
  settings: [],
  supportsFullscreen: true,
  Component: WeekSummary,
};
